import { Popover } from '@headlessui/react';
import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { ReactComponent as DropdownIcon } from 'assets/icons/drop-down.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';

interface SelectSlotDropdownProps {
  control: any;
}

const ITEM_CHECKED = '1';
const ITEM_UNCHECKED = '0';

const SELECT_SLOT_OPTIONS = [
  { label: 'Anytime of Day', value: 'anyTime' },
  { label: '1st Appt slot of Day', value: 'firstSlot' },
  { label: 'Middle Appt slot of Day', value: 'middleSlot' },
  { label: 'Last Appt slot of Day', value: 'lastSlot' },
];

const SelectSlotDropdown: FC<SelectSlotDropdownProps> = ({ control }) => {
  const isValueChecked = (value: string, index: number) => {
    const isChecked = value.charAt(index) === ITEM_CHECKED;
    return isChecked;
  };

  const isDisabled = (value: string, index: number) => {
    const isAnyTimeSelected = value.charAt(0) === ITEM_CHECKED;
    if (isAnyTimeSelected && index !== 0) return true;
    const isOtherSlotSelected = value.slice(1).includes(ITEM_CHECKED);
    if (isOtherSlotSelected && index === 0) return true;
  };

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: string,
    index: number,
    onChange: (...event: any[]) => void
  ) => {
    const isOn = e.target.checked ? ITEM_CHECKED : ITEM_UNCHECKED;
    const updatedValue = value.slice(0, index) + isOn + value.slice(index + 1);
    onChange(updatedValue);
  };

  const getValue = (value: string) => {
    if (value.charAt(0) === ITEM_CHECKED) {
      return SELECT_SLOT_OPTIONS[0].label;
    }

    let message = '';

    for (let i = 1; i < value.length; i++) {
      const isItemChecked = value.charAt(i) === ITEM_CHECKED;
      if (!isItemChecked) continue;

      message += `${SELECT_SLOT_OPTIONS[i].label}, `;
    }

    return message;
  };

  return (
    <Controller
      control={control}
      name="slotShowType"
      rules={{
        validate: (value) => {
          return value !== '0000' || 'This field is required';
        },
      }}
      render={({ field, fieldState: { error } }) => {
        const { value, onChange } = field;

        const isNoValueSelected = value === '0000';

        return (
          <Popover className="relative">
            {({ open }) => (
              <>
                <Popover.Button
                  className={`w-full flex items-center h-4 py-1.3 px-1.6 rounded-[1rem] justify-between border-[0.5px] ${
                    error?.message
                      ? 'shadow-input-error border-[#f44336] hover:border-[#f44336] hover:shadow-input-error'
                      : 'shadow-primary border-lightest-grey hover:border-magenta hover:shadow-input-active'
                  } ${open && !error?.message ? 'shadow-input-active' : ''} 
                    ${isNoValueSelected ? 'text-grey' : ''}`}
                >
                  <span className="truncate">
                    {isNoValueSelected
                      ? 'Select 1st appt slot(s) to show'
                      : getValue(value)}
                  </span>
                  <DropdownIcon
                    className={`${
                      open ? 'fill-[#666]' : 'fill-[#ccc]'
                    } transition-[fill] shrink-0`}
                  />
                </Popover.Button>

                <Popover.Panel className="absolute w-full flex flex-row rounded-[1rem] shadow-primary bg-white z-50 overflow-auto scrollbar text-14 leading-[2.1rem]">
                  <ul className="flex flex-col gap-y-1.6 px-2 py-2.1">
                    {SELECT_SLOT_OPTIONS.map((option, index) => (
                      <li
                        key={option.value}
                        className="flex items-center gap-x-1.6"
                      >
                        <input
                          id={option.value}
                          className="w-1.8 h-1.8 disabled:opacity-60"
                          type="checkbox"
                          checked={isValueChecked(value, index)}
                          onChange={(e) =>
                            handleOnChange(e, value, index, onChange)
                          }
                          disabled={isDisabled(value, index)}
                        />
                        <label
                          className={`text-14 leading-[2.1rem] ${
                            isDisabled(value, index) ? 'opacity-75' : ''
                          } `}
                          htmlFor={option.value}
                        >
                          {option.label}
                        </label>
                      </li>
                    ))}
                  </ul>
                </Popover.Panel>
                {error?.message && (
                  <div className={`flex items-center gap-x-1 mt-1`}>
                    <WarningIcon />
                    <span className="text-12 text-red-01">{error.message}</span>
                  </div>
                )}
              </>
            )}
          </Popover>
        );
      }}
    />
  );
};

export default SelectSlotDropdown;
